import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, vModelText as _vModelText, withDirectives as _withDirectives, renderList as _renderList, Fragment as _Fragment, normalizeClass as _normalizeClass, Transition as _Transition, withCtx as _withCtx } from "vue"

const _hoisted_1 = { class: "permisos-contenido" }
const _hoisted_2 = { class: "permisos-header" }
const _hoisted_3 = { class: "permisos-titulo" }
const _hoisted_4 = {
  key: 0,
  class: "permisos-sub"
}
const _hoisted_5 = { class: "permisos-acciones" }
const _hoisted_6 = ["disabled"]
const _hoisted_7 = { class: "buscador" }
const _hoisted_8 = { key: 0 }
const _hoisted_9 = { class: "categoria-titulo" }
const _hoisted_10 = { class: "permiso-texto" }
const _hoisted_11 = { class: "permiso-nombre" }
const _hoisted_12 = {
  key: 0,
  class: "permiso-desc"
}
const _hoisted_13 = ["aria-checked", "aria-label", "onClick"]
const _hoisted_14 = {
  key: 1,
  class: "vacio"
}
const _hoisted_15 = {
  key: 0,
  class: "barra-guardar"
}
const _hoisted_16 = { class: "barra-acciones" }
const _hoisted_17 = ["disabled"]
const _hoisted_18 = ["disabled"]

import { ref, computed, watch } from 'vue'
import LoadingOverlay from '@/components/LoadingOverlay.vue'
import MensajeToast from '@/components/MensajeToast.vue'
import { obtenerMatrizPermisos, actualizarRol } from '@/services/RegistroUsuariosService'
import { primerError } from '@/services/http'

interface Permiso {
  id: number
  name: string
  nombre: string
  descripcion?: string
  activo: boolean
}
interface Categoria {
  id: number
  nombre: string
  permisos: Permiso[]
}


export default /*@__PURE__*/_defineComponent({
  __name: 'PermisosUsuario',
  props: {
    idRol: {},
    rolLabel: {}
  },
  emits: ["guardado", "cerrar"],
  setup(__props: any, { emit: __emit }) {

/**
 * Matriz de permisos de un usuario.
 *
 * Sirve para OCULTAR acciones que producirían 403; la autorización real la aplica
 * el backend.
 *
 * Componente de presentación. Recibe sus datos por props o del store del módulo.
 */

const props = __props

const emit = __emit

const toast = ref<InstanceType<typeof MensajeToast>>()
const cargando = ref(false)
const guardando = ref(false)
const busqueda = ref('')
const categorias = ref<Categoria[]>([])
const original = ref<Record<number, boolean>>({})

async function cargar() {
  if (!props.idRol) return
  cargando.value = true
  busqueda.value = ''
  try {
    const matriz = await obtenerMatrizPermisos()
    if (!matriz) return

    const rolData = matriz.roles.find(r => r.id === props.idRol)
    const permisosActivos = new Set<string>(rolData?.permisos ?? [])

    categorias.value = matriz.modulos.map(m => ({
      id: m.id,
      nombre: m.nombre,
      permisos: m.permisos.map(p => ({
        id: p.id,
        name: p.name,
        nombre: p.label,
        descripcion: p.descripcion ?? undefined,
        activo: permisosActivos.has(p.name)
      }))
    })).filter(c => c.permisos.length > 0)

    snapshot()
  } finally {
    cargando.value = false
  }
}

function snapshot() {
  const snap: Record<number, boolean> = {}
  categorias.value.forEach(c => c.permisos.forEach(p => { snap[p.id] = p.activo }))
  original.value = snap
}

const categoriasFiltradas = computed<Categoria[]>(() => {
  const q = busqueda.value.trim().toLowerCase()
  if (!q) return categorias.value
  return categorias.value
    .map(cat => ({
      ...cat,
      permisos: cat.permisos.filter(p =>
        p.nombre.toLowerCase().includes(q) ||
        (p.descripcion ?? '').toLowerCase().includes(q)
      )
    }))
    .filter(cat => cat.permisos.length > 0)
})

const hayCambios = computed(() =>
  categorias.value.some(c => c.permisos.some(p => original.value[p.id] !== p.activo))
)

function alternar(perm: Permiso) { perm.activo = !perm.activo }
function borrarTodos() {
  categorias.value.forEach(c => c.permisos.forEach(p => { p.activo = false }))
}
function restablecer() {
  categorias.value.forEach(c => c.permisos.forEach(p => { p.activo = original.value[p.id] ?? false }))
}

async function guardar() {
  guardando.value = true
  try {
    const permisosActivos = categorias.value
      .flatMap(c => c.permisos)
      .filter(p => p.activo)
      .map(p => p.name)

    const result = await actualizarRol(props.idRol, { permisos: permisosActivos })
    if (!result.ok) {
      toast.value?.showToast(primerError(result, 'Error al guardar los permisos.'), 'error')
      return
    }
    snapshot()
    emit('guardado')
  } finally {
    guardando.value = false
  }
}

watch(() => props.idRol, cargar, { immediate: true })

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createVNode(LoadingOverlay, {
      visible: cargando.value,
      message: "Cargando permisos..."
    }, null, 8, ["visible"]),
    _createVNode(MensajeToast, {
      ref_key: "toast",
      ref: toast
    }, null, 512),
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("div", _hoisted_3, [
        _cache[3] || (_cache[3] = _createElementVNode("h2", null, "Permisos del rol", -1)),
        (props.rolLabel)
          ? (_openBlock(), _createElementBlock("p", _hoisted_4, [
              _cache[2] || (_cache[2] = _createTextVNode(" Rol: ")),
              _createElementVNode("strong", null, _toDisplayString(props.rolLabel), 1)
            ]))
          : _createCommentVNode("", true)
      ]),
      _createElementVNode("div", _hoisted_5, [
        _createElementVNode("button", {
          class: "btn",
          onClick: borrarTodos,
          disabled: guardando.value
        }, " Borrar permisos ", 8, _hoisted_6),
        _createElementVNode("button", {
          class: "btn-cerrar",
          onClick: _cache[0] || (_cache[0] = ($event: any) => (emit('cerrar'))),
          "aria-label": "Cerrar"
        }, "✕")
      ])
    ]),
    _createElementVNode("div", _hoisted_7, [
      _withDirectives(_createElementVNode("input", {
        type: "text",
        "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((busqueda).value = $event)),
        placeholder: "Buscar permisos"
      }, null, 512), [
        [_vModelText, busqueda.value]
      ])
    ]),
    (categoriasFiltradas.value.length)
      ? (_openBlock(), _createElementBlock("div", _hoisted_8, [
          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(categoriasFiltradas.value, (cat, ci) => {
            return (_openBlock(), _createElementBlock("div", {
              key: cat.id,
              class: _normalizeClass(["categoria", { 'categoria--primera': ci === 0 }])
            }, [
              _createElementVNode("div", _hoisted_9, _toDisplayString(cat.nombre), 1),
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(cat.permisos, (perm, pi) => {
                return (_openBlock(), _createElementBlock("div", {
                  key: perm.id,
                  class: _normalizeClass(["permiso", { 'permiso--ultimo': pi === cat.permisos.length - 1 }])
                }, [
                  _createElementVNode("div", _hoisted_10, [
                    _createElementVNode("div", _hoisted_11, _toDisplayString(perm.nombre), 1),
                    (perm.descripcion)
                      ? (_openBlock(), _createElementBlock("div", _hoisted_12, _toDisplayString(perm.descripcion), 1))
                      : _createCommentVNode("", true)
                  ]),
                  _createElementVNode("button", {
                    class: _normalizeClass(["toggle", { 'toggle--on': perm.activo }]),
                    role: "switch",
                    "aria-checked": perm.activo,
                    "aria-label": perm.nombre,
                    onClick: ($event: any) => (alternar(perm))
                  }, _cache[4] || (_cache[4] = [
                    _createElementVNode("span", { class: "toggle-bola" }, null, -1)
                  ]), 10, _hoisted_13)
                ], 2))
              }), 128))
            ], 2))
          }), 128))
        ]))
      : (!cargando.value)
        ? (_openBlock(), _createElementBlock("p", _hoisted_14, " No se encontraron permisos" + _toDisplayString(busqueda.value ? ` para "${busqueda.value}"` : '') + ". ", 1))
        : _createCommentVNode("", true),
    _createVNode(_Transition, { name: "slide-up" }, {
      default: _withCtx(() => [
        (hayCambios.value)
          ? (_openBlock(), _createElementBlock("div", _hoisted_15, [
              _cache[5] || (_cache[5] = _createElementVNode("span", { class: "barra-texto" }, "Tienes cambios sin guardar", -1)),
              _createElementVNode("div", _hoisted_16, [
                _createElementVNode("button", {
                  class: "btn",
                  onClick: restablecer,
                  disabled: guardando.value
                }, " Restablecer ", 8, _hoisted_17),
                _createElementVNode("button", {
                  class: "btn",
                  onClick: guardar,
                  disabled: guardando.value
                }, _toDisplayString(guardando.value ? 'Guardando...' : 'Guardar cambios'), 9, _hoisted_18)
              ])
            ]))
          : _createCommentVNode("", true)
      ]),
      _: 1
    })
  ]))
}
}

})